import { useCallback, useMemo, useState } from 'react'
import { Row } from 'react-table'
import cx from 'classnames'
import { getPersonalizedVariantsColumnParams } from 'workflow/common/getColumnParams'
import ResultsTableCell from 'workflow/common/resultsTableCell'

import ConfirmationModal from 'common/components/confirmationModal'
import Loader from 'common/components/loaders/Loader'
import { TableData } from 'common/components/table/Table'
import TableWidget from 'common/components/widget/tableWidget'
import { useAppSelector } from 'common/hooks/redux'
import useContent from 'common/hooks/useContent'
import useSelectedComponentElement from 'common/hooks/useSelectedComponentElement'
import useVariantsToApproveQuery from 'features/unifiedFlow/contentPage/hooks/useVariantsToApproveQuery'

import { PersonalizationRegion } from '../api/api'
import useUpdateVariantStatusMutation from '../api/mutations/useUpdateVariantStatusMutation'
import { VariantResult } from '../api/queries/useGetResultsQuery'

type Props = {
  areVariantsSearched: boolean
  resultType: 'mature' | 'raw'
  data: VariantResult[]
  personalizationRegion: PersonalizationRegion | undefined
  isMachineOpensVisible: boolean
  areResultsFetching: boolean
}

const PersonalizedResultsTable = ({
  areVariantsSearched,
  resultType,
  data,
  personalizationRegion,
  isMachineOpensVisible,
  areResultsFetching,
}: Props) => {
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const [variantToUpdate, setVariantToUpdate] = useState<
    { id: string; variantStatus: string } | undefined
  >(undefined)

  const variantsToApproveQuery = useVariantsToApproveQuery()
  const { isLoading: isContentLoading } = useContent()

  const { data: selectedElement } = useSelectedComponentElement()
  const personalizationId = selectedElement?.personalization_id

  const combinationId = selectedElement?.personalized_variants?.find(
    (variant) => variant.variant_id === variantToUpdate?.id
  )?.combinationId

  const isBaselineVariant = !!selectedElement?.variants?.find(
    (variant) => variant.sl_id === variantToUpdate?.id
  )

  const { isPending: isVariantStatusEditing, mutate } =
    useUpdateVariantStatusMutation({
      accountId,
      personalizationId,
      personalizationRegion,
      resultType,
    })

  const isMatureTabAndAllZeroRecipients =
    (resultType === 'mature' &&
      data?.every((value) => value && value.num_recipients === 0)) ??
    false

  const shouldShowLoader =
    isVariantStatusEditing ||
    variantsToApproveQuery.isLoading ||
    isContentLoading ||
    areResultsFetching

  const tableColumns = useMemo(() => {
    const columns = [
      { title: 'Variants', dataIndex: 'variant', key: 'variant' },
      {
        title: 'Status',
        dataIndex: 'variant_status',
        key: 'variant_status',
      },
      {
        title: 'Sends',
        dataIndex: 'num_recipients',
        key: 'num_recipients',
      },
      { title: 'Opens', dataIndex: 'num_opens', key: 'num_opens' },
      { title: 'Clicks', dataIndex: 'num_clicks', key: 'num_clicks' },
    ]

    return [
      ...columns.map((column) => {
        const { dataIndex: accessor, key: id, title } = column

        return {
          id,
          Header: title,
          accessor,
          Cell: ({ row }) => {
            return (
              <ResultsTableCell
                isLoading={isVariantStatusEditing || areResultsFetching}
                setVariantToUpdate={setVariantToUpdate}
                data={row.original}
                accessor={accessor}
                resultType={resultType}
                isMatureTabAndAllZeroRecipients={
                  isMatureTabAndAllZeroRecipients
                }
                showMachineOpens={isMachineOpensVisible}
              />
            )
          },
          ...getPersonalizedVariantsColumnParams({
            accessor,
            areVariantsSearched,
          }),
        }
      }),
    ]
  }, [
    isVariantStatusEditing,
    resultType,
    isMatureTabAndAllZeroRecipients,
    isMachineOpensVisible,
    areVariantsSearched,
    areResultsFetching,
  ])

  const generateRowClassName = useCallback(
    <T extends TableData>({ row }: { row: Row<T> }) => {
      return cx({
        'bg-gold-40': !row.original.hasError,
      })
    },
    []
  )

  return (
    <>
      <Loader isLoading={shouldShowLoader}>
        <TableWidget.Widget<VariantResult>
          data-testid="personalized-results-table"
          className="border-none bg-gold-40"
          data={data}
          columns={tableColumns}
          generateRowClassName={generateRowClassName}
        />
      </Loader>
      <ConfirmationModal
        data-cy="change-variant-status-modal"
        data-testid="change-variant-status-modal"
        title="Change status of variant?"
        confirmationText="Do you really want to change the status of this variant?"
        cancelButtonText="Cancel"
        confirmButtonText="Yes, do it!"
        isLoading={isVariantStatusEditing}
        confirmButtonVariant="primary"
        open={variantToUpdate !== undefined}
        onConfirm={() => {
          if (!variantToUpdate) {
            return
          }

          if (
            personalizationId &&
            variantToUpdate.id &&
            variantToUpdate.variantStatus
          ) {
            mutate({
              accountId,
              personalizationId,
              combinationId,
              personalizationRegion,
              isBaselineVariant,
              variantId: variantToUpdate.id,
              status: variantToUpdate.variantStatus,
            })
          }

          setVariantToUpdate(undefined)
        }}
        onCancel={() => {
          setVariantToUpdate(undefined)
        }}
      />
    </>
  )
}

export default PersonalizedResultsTable
